import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Sparkles, ArrowRight, MapPin, Briefcase, Coins, TrendingUp, Search } from "lucide-react";
import { FUND_MODEL_LABEL } from "../../lib/taxonomy";
import { MatchScore, BreakdownChips, INVESTOR_HARD_MAX } from "../../components/MatchBadge";
import { t } from "../../lib/i18n";

const formatMoney = (n) => {
  const num = Number(n);
  if (isNaN(num) || !n) return "—";
  if (num >= 1_000_000) return `€${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `€${Math.round(num / 1_000)}K`;
  return `€${num}`;
};


export default function InvestorMarketplace() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [welcome, setWelcome] = useState("");
  const [welcomeCta, setWelcomeCta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await api.get("/investor/marketplace");
      setCompanies(r.data || []);
      setLoading(false);
      api.get("/matches/investor")
        .then((m) => setMatches(m.data || []))
        .catch(() => setMatches([]));
      try {
        const w = await api.post("/ai/welcome");
        setWelcome(w.data.message);
        setWelcomeCta(w.data?.cta_path ? { path: w.data.cta_path, label: w.data.cta_label } : null);
      } catch {}
    })();
  }, []);

  const filtered = companies;

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 space-y-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-9 fade-up">
            <span className="font-mono-label text-gold">{t("Marketplace · Inversores")}</span>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-3">
              {t("Oportunidades de inversión,")}{" "}{user?.name?.split(" ")[0]}
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              {t("Empresas pre-cualificadas con teaser publicado. Expresa interés para abrir la conversación con el searcher y el vendedor.")}
            </p>
          </div>
          <div className="lg:col-span-3 fade-up-delay-1">
            <div className="hairline px-5 py-4 bg-bone">
              <div className="font-mono-label text-muted-foreground">{t("Oportunidades")}</div>
              <div className="font-serif-display text-4xl">{filtered.length}</div>
            </div>
          </div>
        </div>

        {welcome && (
          <div className="hairline p-6 bg-bone fade-up-delay-2 flex items-start gap-4" data-testid="investor-ai-welcome">
            <div className="w-10 h-10 hairline flex items-center justify-center bg-[#1E3A5F] flex-shrink-0">
              <Sparkles className="w-4 h-4 text-gold" />
            </div>
            <div>
              <span className="font-mono-label text-gold">{t("Consejo de tu asesor AI · próximo paso")}</span>
              <p className="font-serif-display text-xl mt-1 leading-snug">{welcome}</p>
              {welcomeCta && (
                <Link to={welcomeCta.path} className="font-mono-label text-[#C25F1E] mt-3 inline-flex items-center gap-1.5 hover:underline underline-offset-4" data-testid="investor-ai-cta">
                  {welcomeCta.label} <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
        )}

        {matches && matches.length > 0 && (
          <section data-testid="investor-match-section">
            <div className="flex items-end justify-between mb-6">
              <div>
                <span className="font-mono-label text-gold flex items-center gap-2">
                  <Sparkles className="w-3 h-3" />{" "}{t("Motor de match triádico")}
                </span>
                <h2 className="font-serif-display text-3xl mt-1" data-testid="investor-match-title">{t("Recomendadas para ti (IA Match)")}</h2>
              </div>
              <Link to="/investor/onboarding" className="font-mono-label text-[#1E3A5F] underline underline-offset-4"
                    data-testid="investor-edit-profile-link">
                {t("Editar criterios")}
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 hairline border-r-0 border-b-0">
              {matches.slice(0, 3).map((m) => <TriangleCard key={m.company.company_id} m={m} />)}
            </div>
          </section>
        )}

        <section data-testid="investor-all-section">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif-display text-3xl" data-testid="investor-all-title">{t("Todas las oportunidades")}</h2>
            {!loading && <span className="font-mono-label text-muted-foreground">{filtered.length}{" "}{t("publicadas")}</span>}
          </div>
        {loading ? (
          <div className="hairline p-12 text-center bg-card">
            <span className="font-mono-label text-muted-foreground">{t("Cargando oportunidades…")}</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="hairline p-12 text-center bg-card">
            <Briefcase className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-serif-display text-2xl mb-2">{t("Aún no hay oportunidades publicadas")}</h3>
            <p className="text-muted-foreground">{t("Estamos sumando nuevas oportunidades cada semana.")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 hairline border-r-0 border-b-0">
            {filtered.map((co) => <OpportunityTile key={co.company_id} co={co} />)}
          </div>
        )}
        </section>
      </div>
    </Layout>
  );
}

const TriangleCard = ({ m }) => (
  <Link
    to={`/investor/opportunity/${m.company.company_id}`}
    className="hairline border-l-0 border-t-0 p-7 bg-card hover:bg-bone transition-colors block group"
    data-testid={`investor-match-card-${m.company.company_id}`}
  >
    <div className="flex items-start justify-between gap-3 mb-4">
      <div>
        <span className="font-mono-label text-gold">{m.company.sector}</span>
        <h3 className="font-serif-display text-2xl leading-tight mt-1">{m.company.name_blind}</h3>
        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
          <MapPin className="w-3 h-3" /> {m.company.country}
        </div>
      </div>
      <MatchScore value={m.triangle_score} label={t("Triángulo")} testid={`investor-triangle-score-${m.company.company_id}`} />
    </div>
    <div className="mb-4">
      <div className="font-mono-label text-muted-foreground mb-2" style={{ fontSize: "0.6rem" }}>
        {t("Tu fit con la empresa ·")}{" "}{m.investor_fit}/100
      </div>
      <BreakdownChips breakdown={m.investor_breakdown} max={INVESTOR_HARD_MAX} />
    </div>
    {m.top_searcher && (
      <div className="hairline-t pt-4 mb-4" data-testid={`investor-top-searcher-${m.company.company_id}`}>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 hairline flex items-center justify-center bg-[#1E3A5F] text-bone flex-shrink-0">
            <Search className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono-label text-muted-foreground" style={{ fontSize: "0.55rem" }}>{t("SEARCHER MEJOR POSICIONADO")}</div>
            <div className="text-sm font-medium truncate">{m.top_searcher.name}</div>
            <div className="text-xs text-muted-foreground truncate">
              {m.top_searcher.current_position}
              {m.top_searcher.fund_model && ` · ${FUND_MODEL_LABEL[m.top_searcher.fund_model] || m.top_searcher.fund_model}`}
              {m.top_searcher.years_experience != null && ` · ${m.top_searcher.years_experience} años`}
            </div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-0 hairline border-r-0" data-testid={`investor-triangle-edges-${m.company.company_id}`}>
          <TriangleEdge label={t("Tú ↔ Empresa")} value={m.investor_fit} />
          <TriangleEdge label={t("Empresa ↔ Searcher")} value={m.top_searcher.match_score} />
          <TriangleEdge label={t("Tú ↔ Searcher")} value={m.top_searcher.investor_searcher_fit} />
        </div>
      </div>
    )}
    <div className="flex items-center justify-between font-mono-label text-[#1E3A5F]">
      <span>{t("Ver oportunidad")}</span>
      <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
    </div>
  </Link>
);

const TriangleEdge = ({ label, value }) => (
  <div className="hairline-r px-2 py-1.5 bg-bone">
    <div className="font-mono-label text-muted-foreground" style={{ fontSize: "0.5rem" }}>{label}</div>
    <div className={`font-serif-display text-base leading-none mt-0.5 ${value >= 75 ? "text-forest" : value >= 50 ? "text-gold" : "text-muted-foreground"}`}>
      {value ?? "—"}
    </div>
  </div>
);

const OpportunityTile = ({ co }) => {
  const facts = co.public_facts || {};
  const fin = co.financial_signal || {};
  const dt = co.deal_terms || {};
  const teaserSummary = (co.teaser_text || "")
    .replace(/^#{1,4}\s+[^\n]+\n*/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\|/g, " ")
    .replace(/\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 160);

  return (
    <Link
      to={`/investor/opportunity/${co.company_id}`}
      className="hairline border-l-0 border-t-0 p-7 bg-card hover:bg-bone transition-colors block group"
      data-testid={`investor-tile-${co.company_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <span className="font-mono-label text-gold">{t("Oportunidad ciega")}</span>
        </div>
        {co.score != null && (
          <div className="hairline px-2 py-1 bg-bone text-right">
            <div className="font-mono-label text-muted-foreground" style={{ fontSize: "0.55rem" }}>{t("SCORE IA")}</div>
            <div className="font-serif-display text-lg leading-none text-gold">{co.score}</div>
          </div>
        )}
      </div>
      <h3 className="font-serif-display text-2xl leading-tight mb-2">{co.name_blind || co.sector}</h3>
      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-4">
        <MapPin className="w-3 h-3" /> {co.country}
      </div>

      {teaserSummary && (
        <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{teaserSummary}…</p>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-3 hairline-t pt-4 mb-4">
        <Mini label="Ask" icon={<Coins className="w-3 h-3" />} value={formatMoney(dt.investment_ask_eur)} />
        <Mini label={t("Equity ofrecido")} icon={<Briefcase className="w-3 h-3" />} value={dt.equity_offered_pct ? `${dt.equity_offered_pct}%` : "—"} />
        <Mini label={t("TIR objetivo")} icon={<TrendingUp className="w-3 h-3" />} value={dt.expected_irr_pct ? `${dt.expected_irr_pct}%` : "—"} />
        <Mini label={t("Margen EBITDA")} value={fin.ebitda_margin_pct != null ? `${fin.ebitda_margin_pct}%` : "—"} />
      </div>

      <div className="flex items-center justify-between font-mono-label text-[#1E3A5F]">
        <span>{t("Ver oportunidad")}</span>
        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
};

const Mini = ({ label, value, icon }) => (
  <div>
    <div className="font-mono-label text-muted-foreground flex items-center gap-1" style={{ fontSize: "0.55rem" }}>
      {icon}{label}
    </div>
    <div className="font-serif-display text-base mt-0.5">{value}</div>
  </div>
);
